import FullPageDrawer from 'compositions/FullPageDrawer';
import Image from 'next/image';
import KnowledgeHubButton from 'components/ui/KnowledgeHubButton';
import RichText from 'components/ui/RichText';
import Text from 'components/ui/Text';
import { FC, useEffect, useState } from 'react';
import { imageDimensions, imageUrl } from 'api';
import {
  CategoryTypes,
  KnowledgeHubItem,
  LightingSolutionCategory,
  MainValueCategory,
  MinMaxConversionTableValues,
  NextArray,
  NormalizedValue,
} from 'type';
import { selectRoomState } from 'store/roomSlice';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import Graph from 'components/Graph';
import { useTranslation } from 'hooks/useTranslation';
import { getNormalizedValueFromMinMax } from 'factories/recipeValueFactory';
import { clampValue } from '@grace-studio/graceful-next/utils';
// import { convertToSlug } from 'utils';
import { selectUiState, setUi } from 'store/uiSlice';

const convertToSlug = (str: string) =>
  str
    ? str
        .toLowerCase()
        //Remove spaces
        .replace(/\s+/g, '-')
        //Remove special characters
        .replace(/[&\/\\#,+()$~%.'":*?<>{}]/g, '')
    : '';

type KnowledgeHubProps = {
  isOpen?: boolean;
  onClose: () => void;
};

const KnowledgeHub: FC<KnowledgeHubProps> = ({ isOpen = false, onClose }) => {
  const currentRoom = useAppSelector(selectRoomState);
  const translation = useTranslation();
  const [knowledgeHubItems, setKnowledgeHubItems] = useState<
    NextArray<KnowledgeHubItem>
  >([]);
  const [selectedKnowledgeHubItem, setSelectedKnowledgeHubItem] =
    useState<KnowledgeHubItem | null>(null);

  const ui = useAppSelector(selectUiState) || {};
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (
      currentRoom?.knowledgeHubItems &&
      currentRoom.knowledgeHubItems.length
    ) {
      setKnowledgeHubItems(currentRoom.knowledgeHubItems);
      // const [firstItem] = currentRoom.knowledgeHubItems;

      const [firstItem] = ui?.dashboard?.lightingKnowledge?.activeId
        ? currentRoom.knowledgeHubItems.filter(
            (solution) =>
              convertToSlug(solution.title) ==
              ui?.dashboard?.lightingKnowledge?.activeId,
          )
        : [...currentRoom.knowledgeHubItems].sort(
            (a, b) => (b.orderRank ?? 0) - (a.orderRank ?? 0),
          );

      setSelectedKnowledgeHubItem(firstItem);
    }
  }, [currentRoom, ui?.dashboard?.lightingKnowledge?.activeId]);

  function handleSidebarClick(item: KnowledgeHubItem) {
    const id = convertToSlug(item.title!);
    dispatch(
      setUi({
        ...ui,
        dashboard: {
          ...ui?.dashboard,
          lightingKnowledge: {
            status: 'open',
            activeId: id,
          },
        },
      }),
    );
  }

  return (
    <FullPageDrawer isOpen={isOpen} onClose={onClose}>
      {selectedKnowledgeHubItem && (
        <>
          {selectedKnowledgeHubItem.heroImage && (
            <div className="relative bg-grey-50 h-40 sm:h-64 md:h-80 lg:h-96">
              <Image
                alt={selectedKnowledgeHubItem.title}
                src={imageUrl(selectedKnowledgeHubItem.heroImage).toString()}
                fill
                className="object-cover"
              />
            </div>
          )}

          <div className="grid grid-cols-[10rem_auto] md:grid-cols-[12rem_auto] lg:grid-cols-[16rem_auto] gap-8 lg:gap-16 mt-8">
            <div>
              <div className="sticky top-[102px]">
                {[...knowledgeHubItems]
                  .sort((a, b) => (b.orderRank ?? 0) - (a.orderRank ?? 0))
                  .map((item) => (
                    <KnowledgeHubButton
                      id={convertToSlug(item.title!)}
                      key={item._key}
                      selected={
                        item._key === (selectedKnowledgeHubItem as any)._key
                      }
                      // onClick={() => setSelectedKnowledgeHubItem(item)}
                      onClick={() => handleSidebarClick(item)}
                    >
                      {item.title}
                    </KnowledgeHubButton>
                  ))}
              </div>
            </div>
            <div className="text-content">
              <Text bold size="xlarge" elementType="h2" className="mb-4">
                {selectedKnowledgeHubItem.title}
              </Text>

              <Text elementType="div" size="preamble" className="mb-8">
                <RichText text={selectedKnowledgeHubItem.preamble} />
              </Text>

              {selectedKnowledgeHubItem.contentBlocks?.map((block) => {
                switch (block._type) {
                  case 'graphBlock':
                    const getHeight = (
                      value: number,
                      category: CategoryTypes,
                      conversionTable?: MinMaxConversionTableValues,
                    ) => {
                      if (!currentRoom) {
                        return 0;
                      }

                      switch (category) {
                        case 'biological':
                        case 'emotional':
                        case 'visual':
                          return value;

                        case 'co2':
                          return getNormalizedValueFromMinMax(value, {
                            ...currentRoom.co2ConversionTable,
                            ...conversionTable,
                          });
                        case 'energyCost':
                          return getNormalizedValueFromMinMax(value, {
                            ...currentRoom.energyCostConversionTable,
                            ...conversionTable,
                          });
                        default:
                        case 'luminaireCost':
                          return getNormalizedValueFromMinMax(value, {
                            ...currentRoom.luminaireCostConversionTable,
                            ...conversionTable,
                          });
                      }
                    };

                    const getColorType = (
                      category: CategoryTypes,
                    ): MainValueCategory => {
                      switch (category) {
                        case 'biological':
                        case 'emotional':
                        case 'visual':
                          return 'human';
                        case 'co2':
                          return 'sustainability';
                        default:
                          return 'financial';
                      }
                    };

                    const bars = block.bars.map((bar) => ({
                      category: 'recessed' as LightingSolutionCategory,
                      large: false,
                      colorType: getColorType(bar.category),
                      title: translation(`recipe.values.${bar.category}`),
                      color: clampValue(
                        Math.ceil(
                          getHeight(
                            bar.value,
                            bar.category,
                            bar.conversionTable,
                          ),
                        ),
                        1,
                        5,
                      ) as NormalizedValue,
                      height:
                        getHeight(
                          bar.value,
                          bar.category,
                          bar.conversionTable,
                        ) * 20,
                    }));

                    return (
                      <div key={block._key} className="mb-8">
                        <Graph bars={bars} />

                        {block.title && (
                          <Text elementType="p" className="mt-4">
                            {block.title}
                          </Text>
                        )}
                      </div>
                    );

                  case 'image':
                    const image = imageUrl(block);
                    const { aspectRatio } = imageDimensions(block);

                    return (
                      <div key={block._key} className="mb-8">
                        <div className="relative" style={{ aspectRatio }}>
                          <Image
                            alt={block.caption || ''}
                            src={image.toString()}
                            fill
                            className="object-contain"
                          />
                        </div>
                        {block.caption && (
                          <Text elementType="p">{block.caption}</Text>
                        )}
                      </div>
                    );

                  case 'contentBlock':
                  default:
                    return (
                      <div
                        key={block._key}
                        className="mb-8"
                        data-section-id={convertToSlug(block.title!)}
                      >
                        {block.title && (
                          <Text
                            bold
                            elementType="h3"
                            size="large"
                            className="mb-4"
                          >
                            {block.title}
                          </Text>
                        )}
                        {block.text && <RichText text={block.text} />}
                      </div>
                    );
                }
              })}
            </div>
          </div>
        </>
      )}
    </FullPageDrawer>
  );
};

export default KnowledgeHub;
